import { format, subHours } from 'date-fns'
import { useTranslation } from 'react-i18next'
import { Fragment, useState, Dispatch, SetStateAction, ChangeEvent, useContext } from 'react'
import { Button, Accordion, AccordionSummary, AccordionDetails, TextField, styled } from '@mui/material'

import Chip from '../common/chip'
import Dialog from '../common/dialog'
import { UserContext } from '../../contexts'
import TrashIcon from '../../assets/icons/trash-icon.svg'
import DangerIcon from '../../assets/icons/danger-icon.svg'
import ClarifyIcon from '../../assets/icons/clarify-icon.svg'
import CommentsIcon from '../../assets/icons/comments-icon.svg'
import DocumentIcon from '../../assets/icons/document-icon.svg'
import { PaymentTagType, SupportTicketStatus } from '../../lib'
import { handleGetMonthLabelByNumber, numberWithCommas } from '../../utils'
import ExpandMoreGreenIcon from '../../assets/icons/expand-more-green-icon.svg'
import { baseButtonBlueBg, baseButtonWhiteBg } from '../../theme/custom-styles'
import { FormError, Payment, PaymentGroup, PaymentTag, SupportTicket } from '../../types'

type PaymentMonthGroup = { amountPaidOnThisPeriod: number; paymentsSentOnThisPeriod: Array<Payment>; periodLabel: string; periodType: string }

const VisuallyHiddenInput = styled('input')({
  clip: 'rect(0 0 0 0)',
  clipPath: 'inset(50%)',
  height: 1,
  overflow: 'hidden',
  position: 'absolute',
  bottom: 0,
  left: 0,
  whiteSpace: 'nowrap',
  width: 1
})

export const PaymentTicketsModule = ({
  errors,
  localErrors,
  paymentData,
  fileInputRef,
  requestToSend,
  responseToSend,
  setRequestToSend,
  setResponseToSend,
  paymentTicketsData,
  paymentTagsToResolve,
  supportTicketAttachments,
  handleDownloadAttachmentFile,
  handleSendCompliancePaymentRequest,
  handleAddAttachmentToSupportTicket,
  handleSendCompliancePaymentResponse,
  handleRemoveSupportTicketAttachment
}: {
  errors: FormError
  fileInputRef: any
  requestToSend: string
  localErrors: FormError
  responseToSend: string
  paymentTagsToResolve: Array<PaymentTag>
  paymentTicketsData?: Array<SupportTicket>
  paymentData: Payment | Partial<Payment> | null
  setRequestToSend: Dispatch<SetStateAction<string>>
  setResponseToSend: Dispatch<SetStateAction<string>>
  handleDownloadAttachmentFile: (attachmentFilename: string) => void
  handleSendCompliancePaymentRequest: (requestToSend: string) => void
  handleRemoveSupportTicketAttachment: (indexToRemove: number) => void
  handleAddAttachmentToSupportTicket: (event: ChangeEvent<HTMLInputElement>) => void
  supportTicketAttachments: Array<{ file: File | null; fileType: string; filename: string; base64: string }>
  handleSendCompliancePaymentResponse: (responseToSend: string, supportTicketAttachments: Array<{ file: File | null; fileType: string; filename: string; base64: string }>) => void
}) => {
  const { userSession } = useContext(UserContext)
  const { t } = useTranslation(['payments-to-review-management', 'document-payments-details-management', 'common'])

  const [warningDetailsModalOpen, setWarningDetailsModalOpen] = useState(false)
  const [paymentTagDataSelected, setPaymentTagDataSelected] = useState<Array<PaymentMonthGroup> | null>(null)

  return (
    <>
      <div
        style={{ borderBottom: 'none' }}
        className="table-row-container compliance-warning-table-row-container"
      >
        <div className="left-content">
          <div className="icon-container">
            <img
              src={ClarifyIcon}
              alt="table-row-icon"
            />
          </div>
          <div className="table-row-content-subtitle">{t('noveltiesTable.complianceProcess')}</div>
        </div>
        <div className="right-content">
          <div className="table-row-info-data payment-to-review-details-danger-text compliance-warning-container">
            {userSession && userSession?.belongsTo === 'holdings' && (
              <>
                {paymentTagsToResolve.map((tagData: PaymentTag, index: number) => {
                  return (
                    <div className="payment-tag-container">
                      <div
                        key={index}
                        className="info-data-container compliance-right-content"
                      >
                        <div className="icon-container">
                          <img
                            src={DangerIcon}
                            alt="danger-icon"
                          />
                        </div>
                        {tagData.type === PaymentTagType.BLACK_LISTED && <div className="info-data-text">{t('complianceWarnings.blacklisted')}</div>}
                        {tagData.type === PaymentTagType.TO_CONFIRM_ORIGIN_OF_FUNDS_DAILY && <div className="info-data-text">{t('complianceWarnings.daily')}</div>}
                        {tagData.type === PaymentTagType.TO_CONFIRM_ORIGIN_OF_FUNDS_WEEKLY && <div className="info-data-text">{t('complianceWarnings.weekly')}</div>}
                        {tagData.type === PaymentTagType.TO_CONFIRM_ORIGIN_OF_FUNDS_MONTHLY && <div className="info-data-text">{t('complianceWarnings.monthly')}</div>}
                        {tagData.type === PaymentTagType.TO_CONFIRM_PREVIOUS_PAYMENT_WAITING_FOR_RESOLUTION && <div className="info-data-text">{t('complianceWarnings.waitingResolutionOfPayment')}</div>}
                      </div>
                      {userSession && String(userSession.subRole) === 'compliance-specialist' && tagData.type !== PaymentTagType.BLACK_LISTED && (
                        <Button
                          variant="text"
                          sx={baseButtonBlueBg}
                          onClick={() => {
                            if (tagData.type === PaymentTagType.TO_CONFIRM_ORIGIN_OF_FUNDS_MONTHLY) {
                              const paymentTagDataToSet = []

                              for (let i = 0; i < tagData.data.length; i++) {
                                const tagDataDetails = tagData.data[i] as PaymentGroup

                                paymentTagDataToSet.push({
                                  periodType: 'monthly',
                                  paymentsSentOnThisPeriod: tagDataDetails.paymentsSentByMonth,
                                  amountPaidOnThisPeriod: Number(tagDataDetails.amountPaidOnTheMonth),
                                  periodLabel: `${handleGetMonthLabelByNumber(tagDataDetails.month.split('-')[1])} ${t('complianceProcess.of')} ${tagDataDetails.month.split('-')[0]}`
                                })
                              }

                              setPaymentTagDataSelected([...paymentTagDataToSet])
                            } else if (tagData.type === PaymentTagType.TO_CONFIRM_ORIGIN_OF_FUNDS_DAILY) {
                              let amountPaidOnThisPeriod = 0
                              for (let i = 0; i < tagData.data.length; i++) {
                                const tagDataDetails = tagData.data[i] as Payment
                                amountPaidOnThisPeriod += Number(tagDataDetails.amount)
                              }

                              setPaymentTagDataSelected([
                                {
                                  periodType: 'daily',
                                  amountPaidOnThisPeriod,
                                  paymentsSentOnThisPeriod: tagData.data as Array<Payment>,
                                  periodLabel: format(subHours(new Date((tagData.data[0] as Payment).createdAt), 5), 'dd/MM/yyyy')
                                }
                              ])
                            } else if (tagData.type === PaymentTagType.TO_CONFIRM_ORIGIN_OF_FUNDS_WEEKLY) {
                              let amountPaidOnThisPeriod = 0
                              for (let i = 0; i < tagData.data.length; i++) {
                                const tagDataDetails = tagData.data[i] as Payment
                                amountPaidOnThisPeriod += Number(tagDataDetails.amount)
                              }

                              setPaymentTagDataSelected([
                                {
                                  periodType: 'weekly',
                                  amountPaidOnThisPeriod,
                                  paymentsSentOnThisPeriod: tagData.data as Array<Payment>,
                                  periodLabel: t('complianceProcess.amountPaidOnWeekSubtitle', { date: format(subHours(new Date((tagData.data[0] as Payment).createdAt), 5), 'dd/MM/yyyy') })
                                }
                              ])
                            } else if (tagData.type === PaymentTagType.TO_CONFIRM_PREVIOUS_PAYMENT_WAITING_FOR_RESOLUTION) {
                              const tagDataDetails = tagData.data as unknown as Payment
                              const amountPaidOnThisPeriod = Number(tagDataDetails.amount)

                              setPaymentTagDataSelected([
                                {
                                  amountPaidOnThisPeriod,
                                  periodType: 'waiting-for-resolution',
                                  paymentsSentOnThisPeriod: [tagData.data] as unknown as Array<Payment>,
                                  periodLabel: t('complianceProcess.paymentForWaitingResolution')
                                }
                              ])
                            }

                            setWarningDetailsModalOpen(true)
                          }}
                        >
                          {t('noveltiesTable.seeDetailsButtonText')}
                        </Button>
                      )}
                    </div>
                  )
                })}
              </>
            )}
          </div>
        </div>
      </div>
      {userSession && userSession.role.indexOf('-client') > -1 && String(paymentData?.status) === 'waiting-for-resolution' && paymentTicketsData?.length === 0 && (
        <div className="comments-warning-container">{t('complianceProcess.commentsWarning')}</div>
      )}
      {((userSession && userSession.role.indexOf('-client') === -1 && paymentTicketsData && paymentTicketsData.length >= 0) ||
        (userSession && userSession.role.indexOf('-client') > -1 && paymentTicketsData && paymentTicketsData.length > 0)) && (
        <>
          <div
            style={{ borderBottom: 'none', padding: '0px 16px', minHeight: '45px' }}
            className="table-row-container compliance-warning-table-row-container"
          >
            <div className="left-content">
              <div className="icon-container">
                <img
                  src={CommentsIcon}
                  alt="table-row-icon"
                />
              </div>
              <div className="table-row-content-subtitle">{`${t('complianceProcess.commentsTitle')}:`}</div>
            </div>
          </div>
          {userSession && userSession.belongsTo === 'holdings' && <div className="comments-section-subtitle">{t('complianceProcess.commentsInstruction')}</div>}
          {paymentTicketsData.length > 0 && (
            <>
              {paymentTicketsData[0].comments?.map((paymentTicketComment: any, index: number) => {
                return (
                  <div
                    key={index}
                    style={{ margin: '0px 16px 20px 48px', padding: '16px 0px', boxSizing: 'border-box', borderBottom: '1px solid #ccc' }}
                  >
                    <div style={{ color: '#737373', lineHeight: '150%' }}>
                      {paymentTicketComment.description.split('\n').map((line: string, index: number) => (
                        <Fragment key={index}>
                          {line}
                          <br />
                        </Fragment>
                      ))}
                      <br />
                      <strong>{`${t('complianceProcess.sentBy')}:`}</strong>
                      {` ${paymentTicketComment.sentBy}${paymentTicketComment.senderRole === 'admin' ? ' | (Equipo de cumplimiento | ADAMO PAY)' : ` | (Equipo de ${paymentTicketComment.companyName})`}`}
                      <br />
                      <strong>{`${t('complianceProcess.date')}:`}</strong> {`${format(new Date(paymentTicketComment.date), 'dd/MM/yyyy | HH:mm:ss')}`}
                    </div>
                    {paymentTicketComment.attachments.length > 0 && (
                      <div style={{ color: '#737373', marginTop: '15px' }}>
                        <strong>{`${t('complianceProcess.attachments')}:`}</strong>
                        {paymentTicketComment.attachments.map((attachmentData: { filename: string; attachmentFileToUploadPreSignedURL: string }, index: number) => {
                          return (
                            <div
                              key={index}
                              onClick={() => handleDownloadAttachmentFile(attachmentData.filename)}
                              style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', margin: '8px 0px', gap: '10px' }}
                            >
                              <img
                                src={DocumentIcon}
                                alt="document-icon"
                              />
                              <div style={{ color: '#00e', textDecorationLine: 'underline' }}>{attachmentData.filename}</div>
                            </div>
                          )
                        })}
                      </div>
                    )}
                  </div>
                )
              })}
            </>
          )}
          {userSession && userSession.belongsTo === 'holdings' && (paymentTicketsData.length === 0 || (paymentTicketsData.length > 0 && paymentTicketsData[0].status === SupportTicketStatus.OPEN)) && (
            <div style={{ padding: '16px 16px 16px 48px', boxSizing: 'border-box', width: '100%' }}>
              <TextField
                minRows={4}
                fullWidth
                multiline
                value={requestToSend || ''}
                error={!!errors.requestToSend}
                helperText={errors.requestToSend}
                onChange={(event) => setRequestToSend(event.target.value)}
                label={t('complianceProcess.textAreaRequestPlaceholder')}
              />
              {errors?.generalError && (
                <div
                  style={{ marginTop: '20px' }}
                  className="error-message"
                >
                  {errors?.generalError}
                </div>
              )}
              <div style={{ width: '100%', marginTop: '20px' }}>
                <Button
                  fullWidth
                  variant="contained"
                  sx={baseButtonWhiteBg}
                  onClick={() => handleSendCompliancePaymentRequest(requestToSend)}
                >
                  {t('complianceProcess.sendRequest')}
                </Button>
              </div>
            </div>
          )}
          {userSession &&
            String(userSession.belongsTo) === String(paymentData?.belongsTo) &&
            userSession.role === 'super-admin-client' &&
            paymentTicketsData.length > 0 &&
            paymentTicketsData[0].status === SupportTicketStatus.OPEN && (
              <div style={{ padding: '16px 16px 16px 48px', boxSizing: 'border-box', width: '100%' }}>
                <TextField
                  minRows={4}
                  fullWidth
                  multiline
                  value={responseToSend || ''}
                  error={!!errors.responseToSend}
                  helperText={errors.responseToSend}
                  onChange={(event) => setResponseToSend(event.target.value)}
                  label={t('complianceProcess.textAreaResponsePlaceholder')}
                />
                {supportTicketAttachments.length > 0 && (
                  <div style={{ marginTop: '15px' }}>
                    {supportTicketAttachments.map((attachmentData: { file: File | null; filename: string; base64: string }, index: number) => {
                      return (
                        <div
                          key={index}
                          style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', margin: '8px 0px' }}
                        >
                          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                            <img
                              src={DocumentIcon}
                              alt="document-icon"
                            />
                            <div style={{ color: '#00e', textDecorationLine: 'underline' }}>{attachmentData.filename}</div>
                          </div>
                          <div
                            style={{ cursor: 'pointer' }}
                            onClick={() => handleRemoveSupportTicketAttachment(index)}
                          >
                            <img
                              src={TrashIcon}
                              alt="trash-icon"
                            />
                          </div>
                        </div>
                      )
                    })}
                  </div>
                )}
                {localErrors?.attachmentFileError && (
                  <div
                    style={{ marginTop: '20px' }}
                    className="error-message"
                  >
                    {localErrors?.attachmentFileError}
                  </div>
                )}
                <div style={{ marginTop: '20px' }}>
                  <Button
                    fullWidth
                    variant="text"
                    component="label"
                    sx={baseButtonBlueBg}
                  >
                    {t('complianceProcess.attachFileToResponse')}
                    <VisuallyHiddenInput
                      type="file"
                      ref={fileInputRef}
                      accept="image/*,application/pdf"
                      onChange={handleAddAttachmentToSupportTicket}
                    />
                  </Button>
                </div>
                {errors?.generalError && (
                  <div
                    style={{ marginTop: '20px' }}
                    className="error-message"
                  >
                    {errors?.generalError}
                  </div>
                )}
                <div style={{ width: '100%', marginTop: '20px' }}>
                  <Button
                    fullWidth
                    variant="contained"
                    sx={baseButtonWhiteBg}
                    onClick={() => handleSendCompliancePaymentResponse(responseToSend, supportTicketAttachments)}
                  >
                    {t('complianceProcess.sendResponse')}
                  </Button>
                </div>
              </div>
            )}

          {userSession &&
            String(userSession.belongsTo) === String(paymentData?.belongsTo) &&
            userSession.role !== 'super-admin-client' &&
            userSession.role.indexOf('-client') > -1 &&
            paymentTicketsData.length > 0 &&
            paymentTicketsData[0].status === SupportTicketStatus.OPEN && <div className="comments-warning-container">{t('paymentAlreadyInProcess.paymentAlreadyInProcess')}</div>}
        </>
      )}
      {warningDetailsModalOpen && (
        <Dialog
          minWidth="800px"
          maxHeight="800px"
          dialogIsLoading={false}
          hideActionsButtons={true}
          dialogTitle={t('complianceProcess.detailsModalTitle')}
          handleDialogOpenState={(openState: boolean) => setWarningDetailsModalOpen(openState)}
        >
          {paymentTagDataSelected?.map((tagData: PaymentMonthGroup, index: number) => {
            return (
              <Accordion
                key={index}
                sx={{ boxShadow: 'none' }}
                defaultExpanded={index === 0}
              >
                <AccordionSummary
                  expandIcon={
                    <img
                      alt="expand-more-icon"
                      src={ExpandMoreGreenIcon}
                      style={{ width: '24px', height: '24px' }}
                    />
                  }
                  id="panel1-header"
                  aria-controls="panel1-content"
                >
                  <div className="accordion-header">
                    <div className="subtitle subtitle-green">
                      {tagData.periodType !== 'waiting-for-resolution' ? `${t('complianceProcess.amountPaidOnTheDaySubtitle')} ${tagData.periodLabel}:` : `${tagData.periodLabel}:`}
                    </div>
                    <div className="subtitle">{`$ ${numberWithCommas(tagData.amountPaidOnThisPeriod)} COP`}</div>
                  </div>
                </AccordionSummary>
                {tagData.paymentsSentOnThisPeriod.length > 0 && (
                  <AccordionDetails>
                    <div className="accordion-content-table-header-container">
                      <div className="accordion-content-table-header-item date">{t('tableHeaders.date')}</div>
                      <div className="accordion-content-table-header-item reference">{t('tableHeaders.reference')}</div>
                      <div className="accordion-content-table-header-item status">{t('tableHeaders.status')}</div>
                      <div className="accordion-content-table-header-item amount">{t('tableHeaders.amount')}</div>
                    </div>
                    {tagData.paymentsSentOnThisPeriod.map((paymentData: Payment) => {
                      return (
                        <div
                          key={paymentData._id}
                          className="accordion-content-table-body-container"
                        >
                          <div className="accordion-content-table-body-item date">{format(new Date(paymentData.createdAt || 0), 'dd/MM/yyyy | HH:mm:ss')}</div>
                          <div className="accordion-content-table-body-item reference">{paymentData.referenceNumber}</div>
                          <div className="accordion-content-table-body-item status">
                            <Chip
                              status={paymentData?.status ?? 'reviewed'}
                              label={
                                paymentData?.status === 'reviewed'
                                  ? t('paymentStatus.reviewed', { ns: 'document-payments-details-management' })
                                  : paymentData?.status?.indexOf('error') !== -1
                                    ? t('paymentStatus.error', { ns: 'document-payments-details-management' })
                                    : paymentData?.status === 'paid'
                                      ? t('paymentStatus.paid', { ns: 'document-payments-details-management' })
                                      : paymentData?.status === 'processing'
                                        ? t('paymentStatus.processing', { ns: 'document-payments-details-management' })
                                        : paymentData?.status === 'rejected'
                                          ? t('paymentStatus.rejected', { ns: 'document-payments-details-management' })
                                          : paymentData?.status === 'waiting-for-resolution'
                                            ? t('paymentStatus.waitingForResolution', { ns: 'document-payments-details-management' })
                                            : paymentData?.status === 'validated'
                                              ? t('paymentStatus.validated', { ns: 'document-payments-details-management' })
                                              : t('paymentStatus.forReview', { ns: 'document-payments-details-management' })
                              }
                            />
                          </div>
                          <div className="accordion-content-table-body-item amount">{`$ ${numberWithCommas(paymentData.amount)} COP`}</div>
                        </div>
                      )
                    })}
                  </AccordionDetails>
                )}
              </Accordion>
            )
          })}
        </Dialog>
      )}
    </>
  )
}
